import { isJobActive, type StoreDesignDetail } from '@studio/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../shell/api.js';
import { subscribeToEvents } from '../shell/stream.js';

const WATCHED = new Set(['candidate', 'candidateBatch', 'candidateSlot', 'generationAttempt', 'technicalCheck', 'job', 'storeDesignExport', 'experiment']);

/** Subscribe before reading; serialize refreshes so an older response never wins a race. */
export function useStoreDesign(id: string) {
  const [value, setValue] = useState<StoreDesignDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reader = useRef<(() => void) | null>(null);
  const refresh = useCallback(() => reader.current?.(), []);
  useEffect(() => {
    let live = true;
    let reading = false;
    let again = false;
    setValue(null);
    const read = (): void => {
      if (reading) { again = true; return; }
      reading = true;
      void api.readStoreDesign(id).then((next) => {
        if (live) { setValue(next); setError(null); }
      }).catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Оформление не удалось прочитать.');
      }).finally(() => {
        reading = false;
        if (live && again) { again = false; read(); }
      });
    };
    reader.current = read;
    const unsubscribe = subscribeToEvents((event) => {
      if ((event.resource === 'storeDesign' && event.id === id) || WATCHED.has(event.resource)) read();
    });
    read();
    return () => { live = false; reader.current = null; unsubscribe(); };
  }, [id]);
  const pending = value?.jobs.some((job) => isJobActive(job.state)) === true
    || value?.exports.some((entry) => isJobActive(entry.state)) === true;
  useEffect(() => {
    if (!pending) return;
    // SSE is immediate; the snapshot also catches a result whose event was lost.
    const timer = window.setInterval(refresh, 10_000);
    return () => window.clearInterval(timer);
  }, [pending, refresh]);
  return { value, error, refresh };
}
